import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { endOfISOWeek, format, startOfISOWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import { fr as frDayPicker } from 'react-day-picker/locale/fr';
import { Box, IconButton, InputBase, Typography, styled, Popover, useTheme } from '@mui/material';
import { formatDateInput, maskDateInput, parseDateInput } from '../helpers/date';
import { ReactComponent as ArrowIcon } from '../assets/icons/24/arrow_large_white.svg';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/24/arrow_left.svg';
import { ReactComponent as CloseIcon } from '../assets/icons/24/close.svg';
import { getFilterButtonBorder, getThemeColors } from '../helpers/themeStyles';
import { THEME_MODES } from '../theme';
import 'react-day-picker/style.css';

const ERROR_COLOR = '#FF6669';

const CustomChevron = ({ orientation, size = 24, className, disabled }) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const isRight = orientation === 'right';

  return (
    <Box
      component={ArrowLeftIcon}
      className={className}
      sx={{
        width: size,
        height: size,
        opacity: disabled ? 0.5 : 1,
        transform: isRight ? 'rotate(180deg)' : 'none',
        display: 'inline-block',
        flexShrink: 0,
        '& path': { fill: colors.textSecondary },
      }}
    />
  );
};

const DateFieldStyled = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== '$open' && prop !== '$error' && prop !== '$focused' && prop !== '$empty',
})(({ theme, $open, $error, $focused, $empty }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: $empty ? '10px 20px' : '6px 20px',
  borderRadius: 40,
  border: $error ? `1px solid ${ERROR_COLOR}` : getFilterButtonBorder(theme, $open || $focused),
  minWidth: 140,
  width: '100%',
  height: 44,
  boxSizing: 'border-box',
  justifyContent: 'space-between',
  color: getThemeColors(theme).textPrimary,
  cursor: 'pointer',
}));

const createPopoverStyles = (theme, highlightIsoWeek) => {
  const colors = getThemeColors(theme);
  const isDark = theme.palette.mode === THEME_MODES.DARK;
  const accent = isDark ? '#B9FF66' : theme.palette.primary.main;

  const shared = {
    padding: 0,
    overflow: 'hidden',
    '& table': { borderSpacing: 0 },
    '& .rdp-day': { border: 'none', padding: 0 },
    '& .rdp-month_caption': {
      margin: 0,
      padding: '12px 16px',
      borderRadius: '6px 6px 0 0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 44,
    },
    '& .rdp-caption_label': {
      color: colors.textPrimary,
      fontWeight: 700,
      fontSize: 16,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    '& .rdp-nav': { display: 'flex', alignItems: 'center', gap: 8 },
    '& .rdp-button_previous, & .rdp-button_next': { color: colors.textSecondary },
    '& .rdp-chevron': { fill: colors.textSecondary },
    '& .rdp-weekday': {
      color: colors.textSecondary,
      textTransform: 'uppercase',
      fontWeight: 500,
      textAlign: 'center',
    },
    '& .rdp-months': { padding: '16px 24px 24px' },
    '& .rdp-root': {
      '--rdp-accent-color': accent,
      '--rdp-accent-background-color': accent,
      '--rdp-day_button-border': 'none',
      '--rdp-selected-border': 'none',
      '--rdp-day-height': '40px',
      '--rdp-day-width': '40px',
      color: colors.textPrimary,
    },
    '& .rdp-day_button': {
      color: colors.textPrimary,
      borderRadius: 20,
    },
  };

  if (!highlightIsoWeek) {
    return {
      ...shared,
      '& .rdp-outside:not(.rdp-selected)': { opacity: 0.3 },
      '& .rdp-selected .rdp-day_button': {
        borderRadius: 20,
        backgroundColor: accent,
        color: colors.activeTextOnAccent,
        fontWeight: 700,
        '&:hover': {
          backgroundColor: accent,
          color: colors.activeTextOnAccent,
        },
      },
    };
  }

  return {
    ...shared,
    '& .rdp-selected .rdp-day_button': { color: colors.activeTextOnAccent },
    '& .rdp-outside:not(.rdp-range_start):not(.rdp-range_end):not(.rdp-range_middle)': {
      opacity: 0.3,
    },
    '& .rdp-root': {
      ...shared['& .rdp-root'],
      '--rdp-range_start-color': colors.activeTextOnAccent,
      '--rdp-range_end-color': colors.activeTextOnAccent,
      '--rdp-range_middle-color': colors.textPrimary,
    },
    '& .rdp-range_start:not(.rdp-range_end) .rdp-day_button': {
      borderTopLeftRadius: 20,
      borderBottomLeftRadius: 20,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    '& .rdp-range_end:not(.rdp-range_start) .rdp-day_button': {
      borderTopRightRadius: 20,
      borderBottomRightRadius: 20,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  };
};

const DateFieldPopoverContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$highlightIsoWeek',
})(({ theme, $highlightIsoWeek }) => createPopoverStyles(theme, $highlightIsoWeek));

const DateField = ({
  sx,
  value,
  onChange,
  onClear,
  label = 'Date',
  placeholder = 'JJ/MM/AAAA',
  dateFormat: _dateFormat = 'medium',
  error = false,
  helperText = '',
  highlightIsoWeek = false,
}) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const inputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputText, setInputText] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const open = Boolean(anchorEl);

  const date = value ? (value instanceof Date ? value : new Date(value)) : undefined;
  const dateKey = date ? formatDateInput(date) : '';

  useEffect(() => {
    if (isInputFocused) return;
    setInputText(dateKey);
  }, [dateKey, isInputFocused]);

  const isoWeekRange = useMemo(() => {
    if (!highlightIsoWeek || value == null) {
      return undefined;
    }
    const d = value instanceof Date ? value : new Date(value);
    return {
      from: startOfISOWeek(d),
      to: endOfISOWeek(d),
    };
  }, [highlightIsoWeek, value]);

  const handleSelect = useCallback(
    (selectedDate) => {
      onChange(selectedDate ?? null);
      if (selectedDate) {
        setInputText(formatDateInput(selectedDate));
        setAnchorEl(null);
      }
    },
    [onChange]
  );

  const handleHighlightIsoWeekSelect = useCallback(
    (nextRange, triggerDate) => {
      if (triggerDate) {
        onChange(triggerDate);
        setInputText(formatDateInput(triggerDate));
        setAnchorEl(null);
        return;
      }
      if (!nextRange?.from) {
        onChange(null);
        setInputText('');
        setAnchorEl(null);
        return;
      }
      const nextDate = nextRange.to ?? nextRange.from;
      onChange(nextDate);
      setInputText(formatDateInput(nextDate));
      setAnchorEl(null);
    },
    [onChange]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const applyTypedDate = useCallback(
    (parsed) => {
      onChange(parsed);
      setInputText(formatDateInput(parsed));
    },
    [onChange]
  );

  const handleInputChange = (event) => {
    const masked = maskDateInput(event.target.value);
    setInputText(masked);
    const parsed = parseDateInput(masked);
    if (!parsed) return;
    applyTypedDate(parsed);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    const parsed = parseDateInput(inputText);
    if (parsed) {
      applyTypedDate(parsed);
      return;
    }
    setInputText(dateKey);
  };

  const handleOpenCalendar = useCallback((event) => {
    if (event.target.closest('[data-date-field-clear]')) return;
    setAnchorEl(event.currentTarget.closest('[data-date-field]') || event.currentTarget);
    inputRef.current?.focus();
  }, []);

  const isEmpty = !inputText && !isInputFocused && !open;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', ...sx }}>
      <DateFieldStyled
        data-date-field
        $open={open}
        $focused={isInputFocused}
        $error={error}
        $empty={isEmpty}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleOpenCalendar}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isEmpty ? 'center' : 'flex-start',
            alignItems: 'flex-start',
            gap: 0,
            flex: 1,
            minWidth: 0,
            height: '100%',
          }}
        >
          <Typography
            sx={{
              fontSize: isEmpty ? 16 : 10,
              fontWeight: 400,
              color: error ? ERROR_COLOR : colors.textCaption,
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
          <InputBase
            inputRef={inputRef}
            value={inputText}
            onChange={handleInputChange}
            onFocus={() => setIsInputFocused(true)}
            onBlur={handleInputBlur}
            placeholder={isEmpty ? '' : placeholder}
            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
            sx={{
              width: '100%',
              fontSize: 12,
              fontWeight: 500,
              color: colors.textPrimary,
              cursor: 'pointer',
              ...(isEmpty && {
                height: 0,
                minHeight: 0,
                overflow: 'hidden',
                opacity: 0,
              }),
              '& .MuiInputBase-input': {
                padding: 0,
                height: isEmpty ? 0 : 'auto',
                cursor: 'pointer',
              },
              '& .MuiInputBase-input::placeholder': {
                color: colors.textCaption,
                opacity: 1,
              },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {date && onClear && (
            <IconButton
              data-date-field-clear
              onClick={(e) => {
                e.stopPropagation();
                onClear();
                setInputText('');
              }}
              sx={{
                p: 0,
                color: colors.textSecondary,
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <Box
            component={ArrowIcon}
            sx={{
              flexShrink: 0,
              display: 'block',
              transform: open ? 'none' : 'rotate(180deg)',
              '& path': { fill: colors.textSecondary },
            }}
          />
        </Box>
      </DateFieldStyled>
      {helperText && (
        <Typography
          sx={{
            fontSize: 12,
            color: error ? ERROR_COLOR : colors.textSecondary,
            paddingLeft: 2,
          }}
        >
          {helperText}
        </Typography>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              background: colors.menuBg,
              borderRadius: 6,
              border: `1px solid ${colors.borderSubtle}`,
              marginTop: 0.5,
            },
          },
        }}
      >
        <DateFieldPopoverContent $highlightIsoWeek={highlightIsoWeek}>
          <DayPicker
            mode={highlightIsoWeek ? 'range' : 'single'}
            selected={highlightIsoWeek ? isoWeekRange : date}
            onSelect={highlightIsoWeek ? handleHighlightIsoWeekSelect : handleSelect}
            defaultMonth={date || new Date()}
            showOutsideDays
            locale={frDayPicker}
            navLayout="around"
            components={{ Chevron: CustomChevron }}
            formatters={{
              formatWeekdayName: (weekday) =>
                format(weekday, 'EEE', { locale: fr }).replace(/\.$/, ''),
            }}
          />
        </DateFieldPopoverContent>
      </Popover>
    </Box>
  );
};

export default DateField;
